import { ITagStatus } from '@tecalisdev/infinity';
import { formatExpirationDate } from '../../core/utils/utils';
import { BOOLEAN_STATUS, IOperationOcrDetail } from '../../processes/models/processes.models';
import { MenuItemAction } from '../../shared/models/master.models';
import { IOperationActivity, OPERATION_STATUS } from '../models/operations.models';

export interface OcrActivityLogContext {
	startAt?: string | null;
	endAt?: string | null;
	logsUrl?: string | null;
	blockedIdentifiersCount?: number;
	/** Precomputado por el facade (`detailKoGeneral`). */
	koGeneral?: { title: string; subtitle: string } | null;
	/** process: AML/PEP con texto OK/KO; ops: sin description. */
	includeAmlPepResultDescription?: boolean;
	/** process: description en FRAUD_ID_BLOCK. */
	includeFraudIdBlockDescription?: boolean;
	screenphotoTitleKey?: string;
	photocopyTitleKey?: string;
	manipulatedPhotoTitleKey?: string;
}

export interface OcrActivityLogDeps {
	translate: (key: string, params?: Record<string, unknown>) => string;
	getTagStatus: (state: string) => ITagStatus;
	shouldDisplayAntifraudCheck: (
		operationState: string | undefined,
		antifraudStatus: BOOLEAN_STATUS | string | undefined,
		options?: { showOnlyKo?: boolean },
	) => boolean;
	canShowOcrImageFraudChecks: () => boolean;
	getDocumentTypeLabel: (documentType?: string | null) => string;
	getCountryLabel: (country?: string | null) => string;
	permanentExpirationLabel: () => string;
}

/** Núcleo de actividad OCR compartido process↔ops (sin section / never-started). */
export function appendOcrActivityLogs(
	activity: IOperationActivity[],
	ocr: IOperationOcrDetail,
	context: OcrActivityLogContext,
	deps: OcrActivityLogDeps,
): void {
	const startAt = context.startAt ?? ocr.start_at;
	if (startAt) {
		activity.push({
			date: startAt,
			description: [deps.translate('PROCESSES.DETAIL.INIT_OPERATION')],
			...(context.logsUrl ? { showLogs: true } : {}),
		});
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.INIT_SCAN'),
			icon: 'icon-card',
			status: ocr.state === OPERATION_STATUS.OK ? BOOLEAN_STATUS.OK : BOOLEAN_STATUS.KO,
		});
	}

	if (deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.DOCUMENT_TYPE)) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.DOCUMENT_TYPE'),
			description: [deps.getDocumentTypeLabel(ocr.document_type)],
			icon: 'icon-card',
			status: ocr.antifraud?.DOCUMENT_TYPE,
		});
	}
	if (deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.COUNTRY)) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.COUNTRY'),
			description: [deps.getCountryLabel(ocr.country)],
			icon: 'icon-list-ticks',
			status: ocr.antifraud?.COUNTRY,
		});
	}
	if (deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.EXPIRATED)) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.DOCUMENT_EXPIRED'),
			description: [formatExpirationDate(ocr.expiration_date, 'dd/MM/yyyy', deps.permanentExpirationLabel())],
			icon: 'icon-list-ticks',
			status: ocr.antifraud?.EXPIRATED,
		});
	}
	if (deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.MRZ_VALIDATE)) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.READ_MRZ'),
			icon: 'icon-resize-viewport',
			status: ocr.antifraud?.MRZ_VALIDATE,
		});
	}
	if (deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.AEAT)) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.AEAT'),
			description: [
				ocr.antifraud?.AEAT === BOOLEAN_STATUS.KO ? deps.translate('PROCESSES.DETAIL.AEAT_ERROR') : '',
			],
			icon: 'icon-resize-viewport',
			status: ocr.antifraud?.AEAT,
		});
	}
	if (deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.AML)) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.AML'),
			...(context.includeAmlPepResultDescription
				? {
						description: [
							deps.translate(
								ocr.antifraud?.AML === BOOLEAN_STATUS.OK
									? 'PROCESSES.DETAIL.AML_RESULT_OK'
									: 'PROCESSES.DETAIL.AML_RESULT_KO',
							),
						],
					}
				: {}),
			icon: 'icon-resize-viewport',
			status: ocr.antifraud?.AML,
		});
	}
	if (deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.PEP)) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.PEP'),
			...(context.includeAmlPepResultDescription
				? {
						description: [
							deps.translate(
								ocr.antifraud?.PEP === BOOLEAN_STATUS.OK
									? 'PROCESSES.DETAIL.PEP_RESULT_OK'
									: 'PROCESSES.DETAIL.PEP_RESULT_KO',
							),
						],
					}
				: {}),
			icon: 'icon-resize-viewport',
			status: ocr.antifraud?.PEP,
		});
	}
	if (deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.PROHIBITED_CAMERAS)) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.PROHIBITED_CAMERAS'),
			icon: 'icon-shield',
			status: ocr.antifraud?.PROHIBITED_CAMERAS,
		});
	}

	const screenphotoKey = context.screenphotoTitleKey ?? 'PROCESSES.DETAIL.SCREENPHOTO';
	const photocopyKey = context.photocopyTitleKey ?? 'PROCESSES.DETAIL.PHOTOCOPY';
	const manipulatedKey = context.manipulatedPhotoTitleKey ?? 'PROCESSES.DETAIL.MANIPULATED_PHOTO';

	if (
		deps.canShowOcrImageFraudChecks() &&
		deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.SCREENPHOTO)
	) {
		activity.push({
			title: deps.translate(screenphotoKey),
			icon: 'icon-shield',
			status: ocr.antifraud?.SCREENPHOTO,
		});
	}
	if (
		deps.canShowOcrImageFraudChecks() &&
		deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.PHOTOCOPY)
	) {
		activity.push({
			title: deps.translate(photocopyKey),
			icon: 'icon-shield',
			status: ocr.antifraud?.PHOTOCOPY,
		});
	}
	if (
		deps.canShowOcrImageFraudChecks() &&
		deps.shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.MANIPULATED_PHOTO)
	) {
		activity.push({
			title: deps.translate(manipulatedKey),
			icon: 'icon-shield',
			status: ocr.antifraud?.MANIPULATED_PHOTO,
		});
	}

	const blockedCount = context.blockedIdentifiersCount ?? 0;
	if (blockedCount > 0) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.FRAUD_ID_BLOCK'),
			...(context.includeFraudIdBlockDescription
				? { description: [deps.translate('PROCESSES.DETAIL.FRAUD_ID_BLOCK_RESULT_KO')] }
				: {}),
			icon: 'icon-shield',
			status: BOOLEAN_STATUS.KO,
		});
	}

	if (context.koGeneral) {
		activity.push({
			title: context.koGeneral.title,
			description: [context.koGeneral.subtitle],
			icon: 'icon-shield',
			status: BOOLEAN_STATUS.KO,
		});
	}

	const endAt = context.endAt ?? ocr.end_at;
	if (endAt) {
		activity.push({
			description: [deps.translate('PROCESSES.DETAIL.FINISH_SCAN')],
			tag: {
				label: deps.translate(`MASTER.STATUS.${ocr.state?.toUpperCase() ?? ''}`),
				status: deps.getTagStatus(ocr.state ?? ''),
			},
			date: endAt,
			link: {
				label: deps.translate('GENERAL.SHOW_RESPONSE'),
				action: MenuItemAction.SHOW_JSON,
				info: { title: deps.translate('PROCESSES.DETAIL.OCR'), data: ocr },
			},
		});
	}
}
